// <s-recently-viewed> remembers product handles the shopper has opened (localStorage) and
// renders the previously viewed ones by cloning the Liquid-authored <template data-card>.
// Product data comes from the storefront product JSON endpoint; the base path is passed
// from Liquid via data-products-url so no route is hardcoded here.
const STORAGE_KEY = "petnest:recently-viewed";

class RecentlyViewed extends HTMLElement {
  async connectedCallback() {
    const current = this.dataset.productHandle;
    const limit = Number(this.dataset.limit) || 8;
    const stored = this.#read();
    const previous = stored.filter((handle) => handle !== current).slice(0, limit);

    if (current) {
      this.#write([current, ...previous].slice(0, limit + 1));
    }

    if (previous.length === 0) return;

    const template = this.querySelector("template[data-card]");
    const list = this.querySelector("[data-list]");
    if (!template || !list) return;

    const base = this.dataset.productsUrl || "";
    const products = await Promise.all(
      previous.map((handle) =>
        fetch(`${base}/${encodeURIComponent(handle)}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null),
      ),
    );

    const formatter = new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: "currency",
      currency: this.dataset.currency || "USD",
    });

    const fragment = document.createDocumentFragment();
    for (const product of products) {
      if (!product) continue;
      const card = template.content.cloneNode(true);
      const link = card.querySelector("[data-link]");
      const image = card.querySelector("[data-image]");
      const title = card.querySelector("[data-title]");
      const price = card.querySelector("[data-price]");
      if (link) {
        link.href = product.url;
        link.setAttribute("aria-label", product.title);
      }
      if (image) {
        if (product.featured_image) {
          const src = product.featured_image.startsWith("//") ? `https:${product.featured_image}` : product.featured_image;
          image.src = `${src}${src.includes("?") ? "&" : "?"}width=600`;
          image.alt = product.title;
        } else {
          image.remove();
        }
      }
      if (title) title.textContent = product.title;
      if (price) price.textContent = formatter.format(product.price / 100);
      fragment.append(card);
    }

    if (!fragment.childNodes.length) return;
    list.append(fragment);
    this.hidden = false;
  }

  #read() {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      return Array.isArray(value) ? value.filter((item) => typeof item === "string") : [];
    } catch {
      return [];
    }
  }

  #write(handles) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch {
      // Storage can be unavailable (private mode); the region simply stays hidden.
    }
  }
}

if (!customElements.get("s-recently-viewed")) {
  customElements.define("s-recently-viewed", RecentlyViewed);
}
